import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Cell, Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import type { StackedNumbersList } from 'src/components/followUp/common/types';

type UseExportOptionsProps = {
  stackLabels: { name: string }[];
  data: StackedNumbersList | null;
  comparisonData: StackedNumbersList | null;
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const useMapRows = () => {
  const { t } = useTranslation('followUp');

  return useCallback(
    (
      data: StackedNumbersList | null,
      stackLabels: { name: string }[],
    ): Row[] => {
      if (!data) return [];

      return [
        [
          {},
          ...stackLabels.map<Cell>(({ name }) => ({
            value: name,
            fontWeight: 'bold',
          })),
        ],
        ...data.results.map((bar) => [
          {
            value: t(`npsGroup.${bar.label}`),
          },
          ...bar.stacks.map((stack) => ({
            value: stack.value,
          })),
        ]),
      ];
    },
    [t],
  );
};

export const useExportOptions = ({
  stackLabels,
  data,
  comparisonData,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const mapRows = useMapRows();

  return useMemo(() => {
    const rows = mapRows(data, stackLabels);
    const comparisonRows = comparisonData
      ? mapRows(comparisonData, stackLabels)
      : undefined;

    return {
      widgetName: widgetName,
      rows,
      comparisonRows,
      queryFilters,
    };
  }, [data, stackLabels, widgetName, queryFilters, mapRows, comparisonData]);
};
